import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import MaterialTable from 'material-table';
import {
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import { checkStatus, handleError, parseJSON } from '../../shared/api/core/common';
import { getBusinessListFromAPI } from '../../shared/data/actions';
import { PaginatedResponse, Business } from '../../shared/models';
import { CustomTableTitle, ErrorStatusBar } from '../../shared/components';
import { EMPTY_ROW_MSG, ERROR_500, ERROR_MSG, NO_PERMISSIONS_ERROR_MSG } from '../../shared/lib/Localization';
import { isSuperAdmin } from '../../shared/lib/PermissionManager';
import {
  initQueryParams,
  replaceHistory,
  tableOptions,
  useQuery,
} from '../../shared/lib/TableQueryParams';
import { enqueueSnackbar, Toast, ToastError, ToastSuccess } from '../../shared/components/Notifier';
import { SVG_ICONS } from '../../shared/icons/SvgIcons';
import { ConversionJob, ConversionJobStatuses, ConversionJobTypes } from '../models';
import { getConversionJobs, retryConversionJob } from '../api';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    select: {
      color: '#0a0a0a',
      marginRight: 4,
      '&:before': {
        borderColor: '#0a0a0a',
      },
      '&:after': {
        borderColor: '#0a0a0a',
      },
      maxWidth: '14rem',
      minWidth: '10rem',
    },
  })
);

const RETRYABLE_STATUSES = new Set(['failed', 'dead', 'mapping_error']);

const ConversionJobsScreen = (props: any) => {
  const classes = useStyles({});
  const [emptyDataSourceMessage, setEmptyDataSourceMessage] = useState(EMPTY_ROW_MSG);
  const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
  const tableRef = useRef<{ onQueryChange: () => void }>();
  const [tableQuery, setTableQuery] = useState(initQueryParams(useQuery(useLocation)));
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [selectedJobType, setSelectedJobType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [expandedIdentifiers, setExpandedIdentifiers] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isSuperAdmin(props)) {
      props.getBusinessList();
    }
  }, []);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }, [selectedBusiness, selectedJobType, selectedStatus]);

  const getJobTypeLabel = (jobType: string) => {
    const found = ConversionJobTypes.find((x) => x.type === jobType);
    return found ? found.value : jobType;
  };

  const getStatusLabel = (status: string) => {
    const found = ConversionJobStatuses.find((x) => x.type === status);
    return found ? found.value : status;
  };

  const handleRetry = (rowData: ConversionJob) => {
    retryConversionJob(rowData.id)
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then(() => {
        props.showToast({ message: `Retry queued for job ${rowData.id}.`, options: ToastSuccess });
        if (tableRef.current) {
          tableRef.current.onQueryChange();
        }
      })
      .catch((error: any) => {
        let msg = 'Retry failed.';
        if (error && error.status) {
          if (error.status === 403 || error.status === 401) {
            msg = NO_PERMISSIONS_ERROR_MSG;
          } else if (error.status === 500) {
            msg = ERROR_500;
          }
        }
        props.showToast({ message: msg, options: ToastError });
      });
  };

  const getIdentifierKey = (jobId: number, identifierType: 'stock-order' | 'customer-order') =>
    `${jobId}-${identifierType}`;

  const toggleIdentifier = (jobId: number, identifierType: 'stock-order' | 'customer-order') => {
    const key = getIdentifierKey(jobId, identifierType);
    setExpandedIdentifiers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderIdentifierCell = (
    rowData: ConversionJob,
    identifierType: 'stock-order' | 'customer-order'
  ) => {
    const key = getIdentifierKey(rowData.id, identifierType);
    const showUuid = !!expandedIdentifiers[key];
    const numberValue =
      identifierType === 'stock-order' ? rowData.stock_order_number : rowData.customer_order_number;
    const uuidValue =
      identifierType === 'stock-order' ? rowData.stock_order_uuid : rowData.customer_order_uuid;
    const fallbackValue = identifierType === 'stock-order' ? rowData.stock_order : rowData.customer_order;
    const displayValue = showUuid ? uuidValue || fallbackValue : numberValue || fallbackValue;
    const canToggle = Boolean(uuidValue || numberValue);

    if (!displayValue) {
      return '-';
    }

    if (!canToggle) {
      return displayValue;
    }

    return (
      <Tooltip title={showUuid ? 'Click to show Number' : 'Click to show UUID'} placement="bottom">
        <span
          style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
          onClick={() => toggleIdentifier(rowData.id, identifierType)}
        >
          {displayValue}
        </span>
      </Tooltip>
    );
  };

  return (
    <div style={{ maxWidth: '100%', marginTop: -16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Typography style={{ paddingRight: 10 }}>Filter by Job Type:</Typography>
        <FormControl>
          <Select
            id="select-conversion-job-type"
            value={selectedJobType}
            onChange={(e) => setSelectedJobType(e.target.value as string)}
            className={classes.select}
          >
            {ConversionJobTypes.map((x) => (
              <MenuItem value={x.type} key={String(x.id)}>
                {x.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Clear job type" placement="bottom">
          <IconButton onClick={() => setSelectedJobType('')}>
            <ClearIcon />
          </IconButton>
        </Tooltip>

        <Typography style={{ paddingRight: 10 }}>Filter by Status:</Typography>
        <FormControl>
          <Select
            id="select-conversion-job-status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as string)}
            className={classes.select}
          >
            {ConversionJobStatuses.map((x) => (
              <MenuItem value={x.type} key={String(x.id)}>
                {x.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Clear status" placement="bottom">
          <IconButton onClick={() => setSelectedStatus('')}>
            <ClearIcon />
          </IconButton>
        </Tooltip>

        <Typography style={{ paddingRight: 10 }}>Filter by Business:</Typography>
        <FormControl>
          <Select
            id="select-conversion-job-business"
            value={selectedBusiness}
            onChange={(e) => setSelectedBusiness(e.target.value as string)}
            className={classes.select}
          >
            {props.businessList &&
              props.businessList.data &&
              props.businessList.data.map((b: Business) => (
                <MenuItem value={b.id} key={String(b.id)}>
                  {b.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Tooltip title="Clear business" placement="bottom">
          <IconButton onClick={() => setSelectedBusiness('')}>
            <ClearIcon />
          </IconButton>
        </Tooltip>
      </div>

      <ErrorStatusBar isVisible={isErrorStatusVisible} />
      <div className="table-wrapper">
        <MaterialTable
          tableRef={tableRef}
          options={{
            ...tableOptions(tableQuery),
            thirdSortClick: false,
            draggable: false,
            sorting: true,
          }}
          localization={{ body: { emptyDataSourceMessage: emptyDataSourceMessage } }}
          columns={[
            { title: 'ID', field: 'id', editable: 'never' },
            { title: 'Business', field: 'business_name', editable: 'never' },
            {
              title: 'Job Type',
              field: 'job_type',
              editable: 'never',
              render: (rowData: any) => getJobTypeLabel(rowData.job_type),
            },
            {
              title: 'Status',
              field: 'status',
              editable: 'never',
              render: (rowData: any) => getStatusLabel(rowData.status),
            },
            { title: 'Attempts', field: 'attempts', editable: 'never' },
            {
              title: 'Stock Order',
              field: 'stock_order_number',
              editable: 'never',
              render: (rowData: any) => renderIdentifierCell(rowData as ConversionJob, 'stock-order'),
            },
            {
              title: 'Customer Order',
              field: 'customer_order_number',
              editable: 'never',
              render: (rowData: any) => renderIdentifierCell(rowData as ConversionJob, 'customer-order'),
            },
            { title: 'Dedupe Key', field: 'dedupe_key', editable: 'never' },
            { title: 'Last Error', field: 'last_error', editable: 'never' },
            { title: 'Created', field: 'created_at', editable: 'never' },
          ]}
          data={(query) =>
            new Promise((resolve) => {
              replaceHistory(query, props.history);
              let pageData = {
                data: [] as ConversionJob[],
                totalCount: 0,
                page: query.page,
              };

              let orderBy = '';
              if (query.orderBy) {
                orderBy = query.orderBy.field as string;
                orderBy = `${query.orderDirection === 'desc' ? '-' : ''}${orderBy}`;
              }

              getConversionJobs(
                query.page + 1,
                query.pageSize,
                query.search,
                selectedBusiness,
                selectedJobType,
                selectedStatus,
                orderBy
              )
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON)
                .then((data: PaginatedResponse<ConversionJob>) => {
                  setErrorStatusVisible(false);
                  setEmptyDataSourceMessage(EMPTY_ROW_MSG);
                  pageData = {
                    data: data.results,
                    totalCount: data.count,
                    page: query.page,
                  };
                  setTableQuery({
                    ...tableQuery,
                    orderBy,
                    totalCount: data.count,
                    page: query.page,
                    pageSize: query.pageSize,
                  });
                  resolve(pageData);
                })
                .catch((error: any) => {
                  if (error && error.status === 500) {
                    setEmptyDataSourceMessage(ERROR_500);
                    setErrorStatusVisible(false);
                  } else if (error && (error.status === 403 || error.status === 401)) {
                    setEmptyDataSourceMessage(ERROR_MSG);
                    setErrorStatusVisible(true);
                  } else {
                    setEmptyDataSourceMessage(ERROR_MSG);
                    setErrorStatusVisible(false);
                  }
                  resolve(pageData);
                });
            })
          }
          title={
            <CustomTableTitle
              history={props.history}
              title={'Conversion Jobs'}
              icon={SVG_ICONS.businessQueue}
              showHelp={true}
              helpText="List of SOCOSR conversion jobs with filters, search and manual retry."
            />
          }
          actions={[
            {
              icon: 'sync',
              tooltip: 'Retry Job',
              onClick: (_event, rowData: any) => {
                const job = rowData as ConversionJob;
                if (!RETRYABLE_STATUSES.has(job.status)) {
                  props.showToast({
                    message: `Job ${job.id} is not retryable in ${job.status} state.`,
                    options: ToastError,
                  });
                  return;
                }
                handleRetry(job);
              },
            },
            {
              icon: 'refresh',
              tooltip: 'Refresh Data',
              isFreeAction: true,
              onClick: () => tableRef.current && tableRef.current.onQueryChange(),
            },
          ]}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  businessList: state.sharedModuleData.businessList,
  userData: state.userLocalData,
});

const mapDispatchToProps = (dispatch: any) => ({
  getBusinessList: () => dispatch(getBusinessListFromAPI()),
  showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConversionJobsScreen);
